import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const GetQuizOfManager = () => {
    const axiosPrivate = useAxiosPrivate();
    const navigate = useNavigate();
    const location = useLocation();

    const [quizDetails, setQuizDetails] = useState({});
    const [errMsg, setErrMsg] = useState("");
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(true);

    const searchParams = new URLSearchParams(location.search);
    const manager = searchParams.get("manager");
    const quizid = searchParams.get("quizid");

    useEffect(() => {
        const fetchQuizDetails = async () => {
            try {
                const response = await axiosPrivate.post(
                    "/admin/getquiz",
                    JSON.stringify({
                        quizid,
                        manager,
                    })
                );

                setSuccess(true);
                setQuizDetails(response?.data?.quizDetails);
            } catch (err) {
                if (err?.response?.status === 404) {
                    setErrMsg("Quiz not found or unauthorized access.");
                } else if (err?.response?.status === 401) {
                    setErrMsg("Invalid or missing authorization token.");
                } else {
                    setErrMsg("Failed to retrieve quiz details.");
                }
            } finally {
                setLoading(false);
            }
        };
        fetchQuizDetails();
    }, [axiosPrivate, manager, quizid]);

    return (
        <div className="fade-in" style={{ maxWidth: "1200px", margin: "0 auto", padding: "2rem 1rem" }}>
            {/* Back Button */}
            <button
                className="btn-secondary"
                style={{ marginBottom: "1.5rem", padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                onClick={() => navigate(`/admin/getquizzes/?manager=${manager}`)}
            >
                ← Back to Quizzes
            </button>

            {/* Header */}
            <div style={{ marginBottom: "2rem" }}>
                <h1
                    style={{
                        fontSize: "2.5rem",
                        fontWeight: "700",
                        marginBottom: "0.5rem",
                        background: "linear-gradient(135deg, var(--primary-purple-light), var(--accent-cyan))",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                    }}
                >
                    Quiz Details
                </h1>
                <p style={{ color: "var(--text-muted)", fontSize: "1.125rem" }}>
                    Viewing details for quiz ID: <span style={{ fontFamily: "monospace" }}>{quizid}</span>
                </p>
            </div>

            {errMsg && <p className="errmsg">{errMsg}</p>}

            {loading ? (
                <div style={{ textAlign: "center", padding: "4rem" }}>
                    <div className="spin" style={{ fontSize: "2rem", marginBottom: "1rem" }}>⏳</div>
                    <p style={{ color: "var(--text-secondary)" }}>Loading details...</p>
                </div>
            ) : (
                <>
                    {success && (
                        <div style={{ display: "grid", gap: "2rem" }}>
                            {/* Info Card */}
                            <div className="card" style={{ padding: "2rem" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "2rem" }}>
                                    <div>
                                        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Quiz Name</p>
                                        <p style={{ fontSize: "1.5rem", fontWeight: "600", color: "var(--text-primary)" }}>{quizDetails?.quizname}</p>
                                    </div>
                                    <div>
                                        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Type</p>
                                        <p style={{ fontSize: "1.5rem", fontWeight: "600", color: "var(--accent-cyan)" }}>{quizDetails?.quiztype}</p>
                                    </div>
                                    <div>
                                        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Credit Limit</p>
                                        <p style={{ fontSize: "1.5rem", fontWeight: "600", color: "var(--warning-amber)" }}>{quizDetails?.creditlimit}</p>
                                    </div>
                                    <div>
                                        <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</p>
                                        <p style={{ fontSize: "1.5rem", fontWeight: "600", color: quizDetails?.status === "start" ? "var(--success-green)" : "var(--error-red)" }}>
                                            {quizDetails?.status === "start" ? "Active" : "Inactive"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Participants Table */}
                            <div>
                                <h2 style={{ fontSize: "1.5rem", fontWeight: "600", marginBottom: "1rem" }}>Participants</h2>
                                {quizDetails?.participants?.length > 0 ? (
                                    <div style={{ overflowX: "auto", borderRadius: "16px", border: "1px solid var(--border)" }}>
                                        <table style={{ width: "100%", minWidth: "800px" }}>
                                            <thead>
                                                <tr>
                                                    <th>S.No</th>
                                                    <th>Username</th>
                                                    <th>Name</th>
                                                    <th>Scores (F-A-C-E)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {quizDetails?.participants?.map((participant, index) => (
                                                    <tr key={participant.user}>
                                                        <td style={{ color: "var(--text-muted)" }}>{index + 1}</td>
                                                        <td style={{ fontWeight: "600", color: "var(--text-primary)" }}>{participant.user}</td>
                                                        <td>{participant.name || "N/A"}</td>
                                                        <td>
                                                            {participant.scores[0] === "NA" ? (
                                                                <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Not Attempted</span>
                                                            ) : (
                                                                <div style={{ display: "flex", gap: "0.5rem" }}>
                                                                    <span style={{ background: "rgba(124, 58, 237, 0.2)", color: "var(--primary-purple-light)", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.875rem" }}>
                                                                        F: {participant.scores[0]}
                                                                    </span>
                                                                    <span style={{ background: "rgba(6, 182, 212, 0.2)", color: "var(--accent-cyan)", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.875rem" }}>
                                                                        A: {participant.scores[1]}
                                                                    </span>
                                                                    <span style={{ background: "rgba(245, 158, 11, 0.2)", color: "var(--warning-amber)", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.875rem" }}>
                                                                        C: {participant.scores[2]}
                                                                    </span>
                                                                    <span style={{ background: "rgba(236, 72, 153, 0.2)", color: "var(--accent-pink)", padding: "0.25rem 0.5rem", borderRadius: "4px", fontSize: "0.875rem" }}>
                                                                        E: {participant.scores[3]}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="card" style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>
                                        No participants found for this quiz.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default GetQuizOfManager;
